import React from 'react';

const toneStyles = {
  green: {
    border: 'border-signal-green/30',
    bg: 'bg-signal-green/5',
    text: 'text-signal-green',
    dot: '#22c55e',
  },
  blue: {
    border: 'border-mentor-accent/30',
    bg: 'bg-mentor-accent/5',
    text: 'text-mentor-accent',
    dot: '#60a5fa',
  },
  yellow: {
    border: 'border-signal-yellow/30',
    bg: 'bg-signal-yellow/5',
    text: 'text-signal-yellow',
    dot: '#eab308',
  },
  orange: {
    border: 'border-signal-orange/30',
    bg: 'bg-signal-orange/5',
    text: 'text-signal-orange',
    dot: '#f97316',
  },
  red: {
    border: 'border-signal-red/30',
    bg: 'bg-signal-red/5',
    text: 'text-signal-red',
    dot: '#ef4444',
  },
};

function ProfileCard({ profile }) {
  const style = toneStyles[profile.tone] || toneStyles.blue;
  const delta = Number(profile.deltaFromMain);
  const deltaLabel = Number.isFinite(delta)
    ? delta === 0
      ? 'Same as main signal'
      : `${delta > 0 ? '+' : ''}${delta} pts vs main`
    : 'Profile lens';

  return (
    <div className={`rounded-2xl border ${style.border} ${style.bg} p-4 h-full`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.dot, boxShadow: `0 0 10px ${style.dot}66` }} />
            <h4 className="font-display font-bold text-sm text-mentor-text">{profile.label}</h4>
          </div>
          <p className="text-[11px] uppercase tracking-wider text-mentor-muted font-display font-bold">{profile.shortLabel}</p>
        </div>
        <div className="text-right">
          <p className={`font-mono font-bold text-lg leading-none ${style.text}`}>{profile.score}</p>
          <p className="text-[10px] text-mentor-muted font-mono">/100</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-3">
        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-mono font-bold uppercase ${style.text} ${style.border}`}>
          {profile.status}
        </span>
        <span className="text-[10px] text-mentor-muted font-mono">{deltaLabel}</span>
      </div>

      <p className="text-xs text-mentor-text/85 font-body leading-relaxed mb-3">{profile.description}</p>
      <div className="rounded-xl border border-mentor-border/35 bg-mentor-bg/35 p-3 mb-3">
        <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold mb-1">Profile Action</p>
        <p className="text-xs text-mentor-text font-body leading-relaxed">{profile.action}</p>
      </div>
      <p className="text-[11px] text-mentor-muted font-body leading-relaxed">{profile.reason}</p>
    </div>
  );
}

export default function InstitutionalRiskProfiles({ profiles = [] }) {
  if (!profiles?.length) return null;

  return (
    <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.38s' }}>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display font-bold text-lg text-mentor-text">Institutional Risk Scoring Profiles</h3>
          <p className="text-sm text-mentor-muted font-body mt-1 max-w-3xl leading-relaxed">
            The main signal stays the source of truth. These profiles show how different investor styles may interpret the same bond-market reading without overriding the confirmed dashboard advice.
          </p>
        </div>
        <div className="rounded-xl border border-mentor-border/50 bg-mentor-bg/35 px-4 py-2">
          <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold">Profile Rule</p>
          <p className="text-xs text-mentor-text font-body">Interpretation layer only</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {profiles.map((profile) => (
          <ProfileCard key={profile.key} profile={profile} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-mentor-border/40 bg-mentor-bg/25 p-4">
        <p className="text-xs text-mentor-muted font-body leading-relaxed">
          <strong className="text-mentor-text">Beginner note:</strong> Different investors can react differently to the same market. A conservative investor may become cautious sooner, while a growth investor may wait for stronger confirmation. Bond Mentor keeps the main signal simple, then uses these profiles to explain risk style differences.
        </p>
      </div>
    </div>
  );
}
